import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams, useLocation } from 'react-router-dom';

import { addNotify } from 'features/notify';
import { actions } from 'features/bonusProgram';

import { searchInItems } from 'features/bonusProgram/data';

import { convertLangById } from 'shared/helpers';

import { convertCreatePromotions } from './convertCreatePromotions';

const getChangedValues = (initial, current) => {
  return Object.keys(current).reduce((acc, key) => {
    if (key === 'imageData' || key === 'bannerData' || key === 'imageUrl' || key === 'bannerUrl') {
      return acc;
    }

    if (current[key] !== initial[key]) {
      acc[key] = current[key];
    }
    return acc;
  }, {});
};

const useCreatePromotions = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const actionProcessing = useSelector(s => s.bonusProgram.actionProcessing);
  const locale = useSelector(s => s.locale.locale);
  const id = useParams().id;
  const promotionById = useSelector(s => s.bonusProgram.promotions.promotionById);

  const [filters, setFilters] = useState({
    lang: searchInItems[1],
  });

  const [state, setState] = useState({
    title: '',
    url: '',
    callToAction: '',
    shortDescription: '',
    fullDescription: '',
    active: false,
    imageData: {
      image: '',
      ext: '',
      error: null,
    },
    imageUrl: '',
    bannerData: {
      image: '',
      ext: '',
      error: null,
    },
    bannerUrl: '',
  });

  const handleTitleChange = title => setState(ps => ({ ...ps, title }));
  const handleUrlChange = url => setState(ps => ({ ...ps, url }));
  const handleCallToActionChange = callToAction => setState(ps => ({ ...ps, callToAction }));
  const handleShortDescriptionChange = shortDescription => setState(ps => ({ ...ps, shortDescription }));
  const handleFullDescriptionChange = fullDescription => setState(ps => ({ ...ps, fullDescription }));
  const handleActiveChange = active => setState(ps => ({ ...ps, active }));
  const handleImageDataChange = imageData => setState(ps => ({ ...ps, imageData }));
  const handleBannerDataChange = bannerData => setState(ps => ({ ...ps, bannerData }));
  const handleCancelClick = () => {
    history.push('/bonus-promo/promotions');
  };

  const handleDescriptionChange = description => {
    if (description.length < 31) {
      setState(ps => ({ ...ps, description }));
    }
  };

  const handleSaveClick = e => {
    e.preventDefault();

    if (id && promotionById) {
      const changedFields = getChangedValues(promotionById, state);

      if (Object.keys(changedFields).length === 0) {
        return;
      }

      dispatch(
        actions.editPromotion(
          id,
          convertCreatePromotions({
            ...changedFields,
            lang: filters.lang.value,
          }),
        ),
      );
    } else {
      dispatch(
        actions.createPromotion(
          convertCreatePromotions({
            ...state,
            lang: filters.lang.value,
          }),
        ),
      );
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(actions.getPromotionById(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (promotionById && id) {
      setState(ps => ({
        ...ps,
        ...promotionById,
        active: promotionById.isActive !== undefined ? promotionById.isActive : promotionById.active,
      }));

      if (promotionById.lang) {
        const langById = convertLangById(promotionById.lang);

        const currentLang = searchInItems.find(item => item.value === langById);
        if (currentLang) {
          setFilters(prev => ({ ...prev, lang: currentLang }));
        }
      }
    }
  }, [promotionById, id]);

  const isImageViable = (state.imageData.image && state.imageData.ext) || state.imageUrl;
  const disabled = !state.name || !state.description || !state.points || actionProcessing || !isImageViable;

  return {
    locale,
    filters,
    values: {
      actionProcessing,
      state,
      disabled,
      promotionById,
    },
    handlers: {
      handleTitleChange,
      handleUrlChange,
      handleCallToActionChange,
      handleShortDescriptionChange,
      handleFullDescriptionChange,
      handleDescriptionChange,
      handleActiveChange,
      handleImageDataChange,
      handleBannerDataChange,
      handleCancelClick,
      handleSaveClick,
      handleSearchInChange: value => {
        const searchInItem = searchInItems.find(item => item.value === value);
        setFilters(prev => ({ ...prev, lang: searchInItem }));
      },
    },
  };
};

export default useCreatePromotions;
